import { Switch, Route, Redirect, Router as WouterRouter, useLocation } from "wouter";
import { wouterParser } from "@/lib/wouter-parser";
import { lazy, Suspense, useState, useEffect, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import DesktopSidebar from "@/components/desktop-sidebar";
import { useSidebarCollapsed } from "@/hooks/useSidebarCollapsed";
import { useFullBleedProfile } from "@/hooks/useFullBleedProfile";
import { useProfileSurroundTheme } from "@/hooks/useProfileSurroundTheme";
import ProfileSidebar, { shouldShowProfileRail } from "@/components/profile-sidebar";
import GlobalMobileSidebar from "@/components/GlobalMobileSidebar";
import GlobalCreateFlow from "@/components/GlobalCreateFlow";
import { GlobalAlgoAdjusterFlow } from "@/components/GlobalAlgoAdjusterFlow";
import MarketingLayout from "@/components/MarketingLayout";
import LoadingSplash from "@/components/LoadingSplash";
import ErrorBoundary from "@/components/ErrorBoundary";
import { I18nContext, getSavedLocale, saveLocale, t, type Locale } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";
import { AudioPlayerProvider, useAudioPlayer } from "@/contexts/AudioPlayerContext";
import MiniPlayer from "@/components/MiniPlayer";
import FullPlayer from "@/components/FullPlayer";
import { trackScreenView } from "@/lib/analytics";
import type { ProfileTheme } from "@scroll/shared-types";

// Route-level code splitting
const Home = lazy(() => import("@/pages/home"));
const Watch = lazy(() => import("@/pages/watch"));
const Discover = lazy(() => import("@/pages/discover"));
const Profile = lazy(() => import("@/pages/profile"));
const Live = lazy(() => import("@/pages/live"));
const Shutdown = lazy(() => import("@/pages/shutdown"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Terms = lazy(() => import("@/pages/terms"));
const Activity = lazy(() => import("@/pages/activity"));
const DeleteAccount = lazy(() => import("@/pages/delete-account"));
const NotFound = lazy(() => import("@/pages/not-found"));
const ThreadView = lazy(() => import("@/components/feed/ThreadView"));
const AdminReports = lazy(() => import("@/pages/admin-reports"));
const AdminUsers = lazy(() => import("@/pages/admin-users"));
const AdminClaims = lazy(() => import("@/pages/admin-claims"));
const AdminUsage = lazy(() => import("@/pages/admin-usage"));
const Bookmarks = lazy(() => import("@/pages/bookmarks"));
const WritePage = lazy(() => import("@/pages/write"));
const WriteDraftsPage = lazy(() => import("@/pages/write-drafts"));
const ArticlePage = lazy(() => import("@/pages/article"));
const ArticleTagPage = lazy(() => import("@/pages/article-tag"));
const SeriesPage = lazy(() => import("@/pages/series"));
const ImportSettingsPage = lazy(() => import("@/pages/settings/import"));
const Onboarding = lazy(() => import("@/pages/onboarding"));
const SettingsIndex = lazy(() => import("@/pages/settings/index"));
const MutedWordsSettings = lazy(() => import("@/pages/settings/muted-words"));
const HiddenPostsSettings = lazy(() => import("@/pages/settings/hidden-posts"));
const ModerationSettings = lazy(() => import("@/pages/settings/moderation"));
const PrivacySettings = lazy(() => import("@/pages/settings/privacy"));
const AccessibilitySettings = lazy(() => import("@/pages/settings/accessibility"));
const LanguageSettings = lazy(() => import("@/pages/settings/language"));
const AppearanceSettings = lazy(() => import("@/pages/settings/appearance"));
const ScrollProSettings = lazy(() => import("@/pages/settings/scroll-pro"));
const PodcastSettingsPage = lazy(() => import("@/pages/settings/podcast-settings"));
const PodcastShowEditPage = lazy(() => import("@/pages/settings/podcast-show-edit"));
const DistributionSettings = lazy(() => import("@/pages/settings/distribution"));
const PodcastEpisodesPage = lazy(() => import("@/pages/settings/podcast-episodes"));
const PodcastEpisodeEditPage = lazy(() => import("@/pages/settings/podcast-episode-edit"));
const PodcastAnalyticsPage = lazy(() => import("@/pages/settings/podcast-analytics"));
const DataExportSettings = lazy(() => import("@/pages/settings/data-export"));
const IdentityVerificationSettings = lazy(() => import("@/pages/settings/identity-verification"));
const AnalyticsPage = lazy(() => import("@/pages/analytics"));
const BuiltInPublic = lazy(() => import("@/pages/built-in-public"));
const Support = lazy(() => import("@/pages/support"));
const SignIn = lazy(() => import("@/pages/sign-in"));
const HashtagPage = lazy(() => import("@/pages/hashtag"));
const PodcastsPage = lazy(() => import("@/pages/podcasts"));
const PodcastDetailPage = lazy(() => import("@/pages/podcast-detail"));
const PodcastSetupPage = lazy(() => import("@/pages/podcast-setup"));
const PublicShowPage = lazy(() => import("@/pages/public-show"));
const NewsPage = lazy(() => import("@/pages/news"));
const NewsArticlePage = lazy(() => import("@/pages/news-article"));
const UniversityHub = lazy(() => import("@/pages/university/index"));
const UniversityTopic = lazy(() => import("@/pages/university/topic"));
const UniversityLesson = lazy(() => import("@/pages/university/lesson"));
const GuidePage = lazy(() => import("@/pages/guide"));
const Streaks = lazy(() => import("@/pages/streaks"));

function PageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-(--scroll-bg)" />
  );
}

function AuthedShell({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user } = useAuth();
  const fullBleed = useFullBleedProfile();
  const showRail = shouldShowProfileRail(location) && !fullBleed;
  const { collapsed } = useSidebarCollapsed();
  useProfileSurroundTheme("authed-shell", (user?.profileTheme ?? null) as ProfileTheme | null, !!user, 0);
  useEffect(() => {
    document.documentElement.style.setProperty("--mini-player-app-left", collapsed ? "60px" : "260px");
    return () => {
      document.documentElement.style.removeProperty("--mini-player-app-left");
    };
  }, [collapsed]);
  const contentConstraint = fullBleed
    ? ""
    : showRail
      ? collapsed
        ? "xl:max-w-[860px] xl:mx-auto"
        : "xl:max-w-[720px] xl:mx-auto"
      : "";
  return (
    <div className="h-screen-safe flex overflow-hidden desktop-feed-theme">
      <DesktopSidebar />
      <div className={`app-content-column flex-1 min-w-0 flex flex-col overflow-hidden ${contentConstraint}`}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </div>
      {!fullBleed && <ProfileSidebar />}
      <GlobalCreateFlow />
      <GlobalAlgoAdjusterFlow />
      <GlobalMobileSidebar />
    </div>
  );
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--scroll-bg)" />
    );
  }

  if (!isAuthenticated) {
    return <Shutdown />;
  }

  return (
    <AuthedShell>
      <Component />
    </AuthedShell>
  );
}

function AdminRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--scroll-bg)" />
    );
  }

  if (!isAuthenticated) {
    return <Shutdown />;
  }

  if (!user?.isAdmin) {
    return (
      <AuthedShell>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-(--scroll-text-secondary) text-sm">You don't have access to this page.</p>
        </div>
      </AuthedShell>
    );
  }

  return (
    <AuthedShell>
      <Component />
    </AuthedShell>
  );
}

function PublicContentPage({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--scroll-bg)" />
    );
  }

  if (!isAuthenticated) {
    return (
      <MarketingLayout>
        <Component />
      </MarketingLayout>
    );
  }

  return (
    <AuthedShell>
      <Component />
    </AuthedShell>
  );
}

function PageWithOptionalSidebar({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--scroll-bg)" />
    );
  }

  if (!isAuthenticated) {
    return <Component />;
  }

  return (
    <AuthedShell>
      <Component />
    </AuthedShell>
  );
}

function MarketingPage({ component: Component }: { component: React.ComponentType }) {
  return <Component />;
}

const routeTitles: Record<string, string> = {
  "/home": "Home",
  "/search": "Search",
  "/discover": "Search",
  "/bookmarks": "Bookmarks",
  "/activity": "Activity",
  "/streaks": "Streaks",
  "/watch": "Watch",
  "/live": "Live",
  "/analytics": "Analytics",
  "/podcasts": "Podcasts",
  "/news": "News",
  "/privacy": "Privacy Policy",
  "/terms": "Terms of Service",
  "/features": "Features",
  "/built-in-public": "Built in Public",
  "/podcasters": "Podcasts",
  "/brand": "Brand",
  "/ambassadors": "Ambassadors",
  "/support": "Support",
  "/delete-account": "Delete Account",
  "/onboarding": "Welcome",
  "/settings/muted-words": "Muted Words",
  "/settings/hidden-posts": "Hidden Posts",
  "/settings/moderation": "Moderation",
  "/settings/privacy": "Privacy Settings",
  "/settings/accessibility": "Accessibility",
  "/settings/language": "Language",
  "/settings/appearance": "Appearance",
  "/settings/scroll-pro": "Scroll Pro",
  "/settings/podcast": "Podcast Settings",
  "/settings/distribution": "Distribution",
  "/settings/podcast-episodes": "Published Episodes",
  "/settings/podcast-analytics": "Podcast Analytics",
  "/settings/data-export": "Data Export",
  "/settings/identity-verification": "Identity Verification",
  "/admin": "Admin — Feature Usage",
  "/admin/": "Admin — Feature Usage",
  "/admin/reports": "Admin — Reports",
  "/admin/users": "Admin — Users",
  "/admin/claims": "Admin — Claims",
  "/admin/usage": "Admin — Feature Usage",
};

const DEFAULT_TITLE = "Scroll — Podcast Hosting & Promotion";

function ScrollToTop() {
  const [location] = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Cross-page hash links (e.g. /features#video) — wait for the lazy
      // route + sections to render, then scroll the anchor into view.
      const id = decodeURIComponent(hash.slice(1));
      let tries = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
        if (++tries < 20) setTimeout(tryScroll, 100);
      };
      setTimeout(tryScroll, 60);
    } else {
      window.scrollTo(0, 0);
    }
    const title = routeTitles[location];
    document.title = title ? `${title} — Scroll` : DEFAULT_TITLE;
    if (isAuthenticated) {
      trackScreenView(location, { title: title ?? "Unknown" });
    }
  }, [location, isAuthenticated]);
  return null;
}

function Router() {
  return (
    <>
    <ScrollToTop />
    <Switch>
      <Route path="/privacy">
        <MarketingPage component={Privacy} />
      </Route>
      <Route path="/terms">
        <MarketingPage component={Terms} />
      </Route>
      <Route path="/guidelines">
        <Redirect to="/terms" />
      </Route>
      <Route path="/features">
        <MarketingPage component={Shutdown} />
      </Route>
      <Route path="/why-scroll">
        <Redirect to="/features" />
      </Route>
      <Route path="/built-in-public">
        <MarketingPage component={BuiltInPublic} />
      </Route>
      <Route path="/podcasters">
        <MarketingPage component={Shutdown} />
      </Route>
      <Route path="/writers">
        <Redirect to="/podcasters" />
      </Route>
      <Route path="/brand">
        <MarketingPage component={Shutdown} />
      </Route>
      <Route path="/ambassadors">
        <MarketingPage component={Shutdown} />
      </Route>
      <Route path="/support">
        <MarketingPage component={Support} />
      </Route>
      <Route path="/creator-promise">
        <Redirect to="/podcasters" />
      </Route>
      <Route path="/for-creators">
        <Redirect to="/podcasters" />
      </Route>
      <Route path="/download">
        <MarketingPage component={Shutdown} />
      </Route>
      <Route path="/guides/:slug">
        <MarketingPage component={GuidePage} />
      </Route>
      {/* Unlinked owner entry — web login is otherwise disabled (mobile-first) */}
      <Route path="/sign-in">
        <MarketingPage component={SignIn} />
      </Route>
      <Route path="/delete-account">
        <MarketingPage component={DeleteAccount} />
      </Route>
      <Route path="/onboarding">
        <Onboarding />
      </Route>
      <Route path="/post/:postId">
        <PublicContentPage component={ThreadView} />
      </Route>
      <Route path="/write/drafts">
        <ProtectedRoute component={WriteDraftsPage} />
      </Route>
      <Route path="/write/:articleId">
        <ProtectedRoute component={WritePage} />
      </Route>
      <Route path="/write">
        <ProtectedRoute component={WritePage} />
      </Route>
      <Route path="/a/:articleId">
        <PublicContentPage component={ArticlePage} />
      </Route>
      <Route path="/@:handle/tag/:tagSlug">
        <PublicContentPage component={ArticleTagPage} />
      </Route>
      <Route path="/@:handle/series/:seriesSlug">
        <PublicContentPage component={SeriesPage} />
      </Route>
      <Route path="/@:handle/:slug">
        <PublicContentPage component={ArticlePage} />
      </Route>
      <Route path="/hashtag/:name">
        <PublicContentPage component={HashtagPage} />
      </Route>
      <Route path="/search">
        <ProtectedRoute component={Discover} />
      </Route>
      <Route path="/discover">
        <Redirect to="/search" />
      </Route>
      <Route path="/bookmarks">
        <ProtectedRoute component={Bookmarks} />
      </Route>
      <Route path="/profile/:id?">
        <ProtectedRoute component={Profile} />
      </Route>
      <Route path="/activity">
        <ProtectedRoute component={Activity} />
      </Route>
      <Route path="/streaks">
        <ProtectedRoute component={Streaks} />
      </Route>
      <Route path="/notifications">
        <Redirect to="/activity" />
      </Route>
      <Route path="/watch">
        <ProtectedRoute component={Watch} />
      </Route>
      <Route path="/live/:id?">
        <ProtectedRoute component={Live} />
      </Route>
      <Route path="/admin">
        <Redirect to="/admin/usage" />
      </Route>
      <Route path="/admin/">
        <Redirect to="/admin/usage" />
      </Route>
      <Route path="/admin/reports">
        <AdminRoute component={AdminReports} />
      </Route>
      <Route path="/admin/users">
        <AdminRoute component={AdminUsers} />
      </Route>
      <Route path="/admin/claims">
        <AdminRoute component={AdminClaims} />
      </Route>
      <Route path="/admin/usage">
        <AdminRoute component={AdminUsage} />
      </Route>
      <Route path="/settings">
        <ProtectedRoute component={SettingsIndex} />
      </Route>
      <Route path="/settings/muted-words">
        <ProtectedRoute component={MutedWordsSettings} />
      </Route>
      <Route path="/settings/hidden-posts">
        <ProtectedRoute component={HiddenPostsSettings} />
      </Route>
      <Route path="/settings/moderation">
        <ProtectedRoute component={ModerationSettings} />
      </Route>
      <Route path="/settings/privacy">
        <ProtectedRoute component={PrivacySettings} />
      </Route>
      <Route path="/settings/accessibility">
        <ProtectedRoute component={AccessibilitySettings} />
      </Route>
      <Route path="/settings/language">
        <ProtectedRoute component={LanguageSettings} />
      </Route>
      <Route path="/settings/appearance">
        <ProtectedRoute component={AppearanceSettings} />
      </Route>
      <Route path="/settings/scroll-pro">
        <ProtectedRoute component={ScrollProSettings} />
      </Route>
      <Route path="/settings/podcast">
        <ProtectedRoute component={PodcastSettingsPage} />
      </Route>
      <Route path="/settings/podcast/:id">
        <ProtectedRoute component={PodcastShowEditPage} />
      </Route>
      <Route path="/settings/distribution">
        <ProtectedRoute component={DistributionSettings} />
      </Route>
      <Route path="/settings/podcast-episodes/:postId">
        <ProtectedRoute component={PodcastEpisodeEditPage} />
      </Route>
      <Route path="/settings/podcast-episodes">
        <ProtectedRoute component={PodcastEpisodesPage} />
      </Route>
      <Route path="/settings/podcast-analytics">
        <ProtectedRoute component={PodcastAnalyticsPage} />
      </Route>
      <Route path="/settings/data-export">
        <ProtectedRoute component={DataExportSettings} />
      </Route>
      <Route path="/settings/import">
        <ProtectedRoute component={ImportSettingsPage} />
      </Route>
      <Route path="/settings/identity-verification">
        <ProtectedRoute component={IdentityVerificationSettings} />
      </Route>
      <Route path="/analytics">
        <ProtectedRoute component={AnalyticsPage} />
      </Route>
      <Route path="/podcasts">
        <ProtectedRoute component={PodcastsPage} />
      </Route>
      <Route path="/podcast-setup">
        <ProtectedRoute component={PodcastSetupPage} />
      </Route>
      <Route path="/podcast/:id">
        <ProtectedRoute component={PodcastDetailPage} />
      </Route>
      <Route path="/show/:slug">
        <PageWithOptionalSidebar component={PublicShowPage} />
      </Route>
      <Route path="/home">
        <ProtectedRoute component={Home} />
      </Route>
      <Route path="/">
        <Shutdown />
      </Route>
      <Route path="/news/:slug">
        <MarketingPage component={NewsArticlePage} />
      </Route>
      <Route path="/news">
        <MarketingPage component={NewsPage} />
      </Route>
      <Route path="/university/:topicSlug/:lessonSlug">
        <MarketingPage component={UniversityLesson} />
      </Route>
      <Route path="/university/:topicSlug">
        <MarketingPage component={UniversityTopic} />
      </Route>
      <Route path="/university">
        <MarketingPage component={UniversityHub} />
      </Route>
      <Route path="/:username">
        <PublicContentPage component={Profile} />
      </Route>
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function AppContent() {
  const [showSplash, setShowSplash] = useState(true);
  const [showFullPlayer, setShowFullPlayer] = useState(false);
  const { isAuthenticated } = useAuth();
  const { currentEpisode } = useAudioPlayer();

  useEffect(() => {
    // Show splash for a minimum duration so the animation completes
    const timer = setTimeout(() => setShowSplash(false), 1800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const openFullPlayer = () => setShowFullPlayer(true);
    window.addEventListener("scroll:open-full-player", openFullPlayer);
    return () => window.removeEventListener("scroll:open-full-player", openFullPlayer);
  }, []);

  return (
    <>
      <LoadingSplash isVisible={showSplash && isAuthenticated} />
      <div className="min-h-screen bg-(--scroll-bg) text-(--scroll-text)">
        <Toaster />
        <Suspense fallback={<PageSpinner />}>
          <WouterRouter parser={wouterParser}>
            <Router />
          </WouterRouter>
        </Suspense>
        {currentEpisode && (
          <>
            <MiniPlayer onExpand={() => setShowFullPlayer(true)} />
            {showFullPlayer && <FullPlayer onClose={() => setShowFullPlayer(false)} />}
          </>
        )}
      </div>
    </>
  );
}

function App() {
  const [locale, setLocaleState] = useState<Locale>(getSavedLocale);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    saveLocale(newLocale);
  }, []);

  const translate = useCallback(
    (key: string, params?: Record<string, string | number>) => t(key, locale, params),
    [locale],
  );

  return (
    <Sentry.ErrorBoundary fallback={<PageSpinner />}>
      <I18nContext.Provider value={{ locale, setLocale, t: translate }}>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <AudioPlayerProvider>
                <AppContent />
              </AudioPlayerProvider>
            </TooltipProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </I18nContext.Provider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
